import { QUOTE_SOURCE_FIELDS } from './businessFieldDirectory';
import type { QuoteChannelProjection } from './quoteChannelProjection';
import { quoteTotal } from './quoteTotals';
import type {
  CpqState,
  CustomFieldOwner,
  FrozenCustomerSnapshot,
  FrozenQuotationCell,
  FrozenQuotationCellValue,
  FrozenQuotationFieldDescriptor,
  FrozenQuotationHeaderProjection,
  FrozenQuotationListProjection,
  FrozenQuotationListRow,
  PublicShareItemSnapshot,
  QuoteFieldPlacement,
  QuoteFieldType,
  QuotePlacementValueSnapshot,
  ShareFieldCombinationSnapshot,
  ShareFieldDefinitionSnapshot,
} from './types';
import {
  createSystemFieldRegistry,
  createUnifiedFieldRegistry,
  quotePlacementForOwner,
} from './unifiedFieldRegistry';

type FrozenProjectionBundle = {
  quotationListProjection: FrozenQuotationListProjection;
  headerProjection: FrozenQuotationHeaderProjection;
};

type OutputSourceBinding = {
  fieldKey: string;
  owner: CustomFieldOwner;
  valueType: QuoteFieldType;
  placement: QuoteFieldPlacement;
};

/**
 * These non-definition fields do not have a catalog definition, so their
 * stable output identity lives in this one schema binding.  No React page is
 * allowed to infer a field from its label or from a QuoteItem property.
 */
const OUTPUT_SOURCE_BINDINGS: Readonly<Record<string, OutputSourceBinding>> = {
  [QUOTE_SOURCE_FIELDS.deviceId]: {
    fieldKey: 'quote_item_device_id',
    owner: 'quote-line',
    valueType: 'text',
    placement: 'quote-line',
  },
  [QUOTE_SOURCE_FIELDS.sourceType]: {
    fieldKey: 'quote_item_source_type',
    owner: 'quote-line',
    valueType: 'text',
    placement: 'quote-line',
  },
  [QUOTE_SOURCE_FIELDS.sourceId]: {
    fieldKey: 'quote_item_source_id',
    owner: 'quote-line',
    valueType: 'text',
    placement: 'quote-line',
  },
  [QUOTE_SOURCE_FIELDS.sourceName]: {
    fieldKey: 'quote_item_source_name',
    owner: 'quote-line',
    valueType: 'text',
    placement: 'quote-line',
  },
  [QUOTE_SOURCE_FIELDS.remark]: {
    fieldKey: 'quote_item_remark',
    owner: 'quote-line',
    valueType: 'text',
    placement: 'quote-line',
  },
};

const QUOTE_FIELD_TYPES: readonly QuoteFieldType[] = [
  'text',
  'number',
  'money',
  'percent',
  'code',
  'date',
  'single-select',
  'multi-select',
  'boolean',
];

const QUOTE_FIELD_PLACEMENTS: readonly QuoteFieldPlacement[] = [
  'quote-line',
  'package-summary',
  'quote-header',
];

const FIELD_OWNERS: readonly CustomFieldOwner[] = [
  'device',
  'package',
  'plan',
  'customer',
  'quote-header',
  'quote-line',
];

function sameStringOrder(left: readonly string[], right: readonly string[]) {
  return (
    left.length === right.length &&
    left.every((item, index) => item === right[index])
  );
}

function assertNonEmptyString(
  value: unknown,
  message: string,
): asserts value is string {
  if (typeof value !== 'string' || !value.trim()) throw new Error(message);
}

function assertUniqueFieldIds(fieldIds: readonly string[], message: string) {
  if (
    fieldIds.length === 0 ||
    new Set(fieldIds).size !== fieldIds.length ||
    fieldIds.some((fieldId) => !fieldId.trim())
  )
    throw new Error(message);
}

function isQuotedValue(value: unknown): value is FrozenQuotationCellValue {
  return (
    value === null ||
    typeof value === 'string' ||
    (typeof value === 'number' && Number.isFinite(value)) ||
    typeof value === 'boolean' ||
    (Array.isArray(value) && value.every((item) => typeof item === 'string'))
  );
}

function isValueForType(value: FrozenQuotationCellValue, type: QuoteFieldType) {
  if (value === null) return true;
  if (type === 'number' || type === 'money' || type === 'percent')
    return typeof value === 'number' && Number.isFinite(value);
  if (type === 'boolean') return typeof value === 'boolean';
  if (type === 'multi-select')
    return (
      Array.isArray(value) && value.every((item) => typeof item === 'string')
    );
  return typeof value === 'string';
}

/** Converts old display-shaped channel values back to their frozen typed form. */
function normaliseValue(
  value: unknown,
  type: QuoteFieldType,
): FrozenQuotationCellValue {
  if (value === undefined || value === null) return null;
  if (type === 'number' || type === 'money' || type === 'percent') {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const numeric = value.replace(/[￥¥,%\s]/g, '');
      if (numeric && /^-?(?:\d+|\d*\.\d+)$/.test(numeric)) {
        const parsed = Number(numeric);
        if (Number.isFinite(parsed)) return parsed;
      }
    }
    throw new Error(`冻结字段值类型不匹配：${type}`);
  }
  if (type === 'boolean') {
    if (typeof value === 'boolean') return value;
    if (value === '是') return true;
    if (value === '否') return false;
    throw new Error('冻结布尔字段值类型不匹配');
  }
  if (type === 'multi-select') {
    if (Array.isArray(value) && value.every((item) => typeof item === 'string'))
      return [...value];
    if (typeof value === 'string') return value ? value.split('；') : [];
    throw new Error('冻结多选字段值类型不匹配');
  }
  if (typeof value === 'string') return value;
  throw new Error(`冻结字段值类型不匹配：${type}`);
}

function cell(value: unknown, valueType: QuoteFieldType): FrozenQuotationCell {
  const normalised = normaliseValue(value, valueType);
  return { valueType, value: normalised };
}

function isDescriptorPlacementForList(
  placement: QuoteFieldPlacement,
): placement is 'quote-line' | 'package-summary' {
  return placement === 'quote-line' || placement === 'package-summary';
}

function selectedFieldIds(snapshot: ShareFieldCombinationSnapshot): string[] {
  const selected = snapshot.selectedFieldIds ?? snapshot.fieldIds;
  assertUniqueFieldIds(selected, '分享字段组合缺少稳定选择字段');
  if (selected.some((fieldId) => !snapshot.fieldIds.includes(fieldId)))
    throw new Error('分享字段组合选择字段不属于冻结字段集');
  return [...selected];
}

function fieldSnapshotById(snapshot: ShareFieldCombinationSnapshot) {
  assertUniqueFieldIds(snapshot.fieldIds, '分享字段组合冻结字段无效');
  if (
    snapshot.fields.length !== snapshot.fieldIds.length ||
    new Set(snapshot.fields.map((field) => field.fieldId)).size !==
      snapshot.fields.length
  )
    throw new Error('分享字段组合定义快照不完整');
  const fields = new Map(
    snapshot.fields.map((field) => [field.fieldId, field]),
  );
  if (snapshot.fieldIds.some((fieldId) => !fields.has(fieldId)))
    throw new Error('分享字段组合字段顺序与定义不一致');
  return fields;
}

function fieldDescriptor(
  field: ShareFieldDefinitionSnapshot,
  placement: QuoteFieldPlacement,
  binding:
    | OutputSourceBinding
    | {
        fieldKey: string;
        owner: CustomFieldOwner;
        valueType: QuoteFieldType;
      },
): FrozenQuotationFieldDescriptor {
  if (field.type !== binding.valueType)
    throw new Error(`冻结字段类型与稳定绑定不一致：${field.fieldId}`);
  if (field.placement && field.placement !== placement)
    throw new Error(`冻结字段位置与输出投影不一致：${field.fieldId}`);
  return {
    fieldId: field.fieldId,
    fieldKey: binding.fieldKey,
    owner: binding.owner,
    valueType: binding.valueType,
    label: field.label,
    width: field.width,
    placement,
    sourceKind: field.sourceKind,
    ...(field.definitionVersion
      ? { definitionVersion: field.definitionVersion }
      : {}),
    ...(field.formulaVersion ? { formulaVersion: field.formulaVersion } : {}),
    presentation: binding.fieldKey === 'device_image' ? 'image' : 'value',
    customerEdit: binding.fieldKey === 'quantity' ? 'quantity' : 'read-only',
  };
}

function descriptorForCurrentProjection(
  state: CpqState,
  field: ShareFieldDefinitionSnapshot,
  placement: QuoteFieldPlacement,
): FrozenQuotationFieldDescriptor {
  const sourceBinding = OUTPUT_SOURCE_BINDINGS[field.fieldId];
  if (sourceBinding) {
    if (sourceBinding.placement !== placement)
      throw new Error(`输出来源字段位置不匹配：${field.fieldId}`);
    return fieldDescriptor(field, placement, sourceBinding);
  }
  if (!field.fieldId.startsWith('definition:'))
    throw new Error(`未知冻结输出字段身份：${field.fieldId}`);
  const fieldKey = field.fieldId.slice('definition:'.length);
  const descriptor = createUnifiedFieldRegistry(
    state.catalog.quoteFieldDefinitions,
  ).find((candidate) => candidate.definition.key === fieldKey);
  if (!descriptor)
    throw new Error(`冻结字段未绑定统一 Registry：${field.fieldId}`);
  const expectedPlacement =
    descriptor.binding.quotePlacement ??
    quotePlacementForOwner(descriptor.owner, fieldKey);
  if (!expectedPlacement || expectedPlacement !== placement)
    throw new Error(`冻结字段 owner 或位置不匹配：${field.fieldId}`);
  if (
    descriptor.definition.type !== field.type ||
    descriptor.definition.sourceKind !== field.sourceKind
  )
    throw new Error(`冻结字段定义与统一 Registry 不一致：${field.fieldId}`);
  return fieldDescriptor(field, placement, {
    fieldKey,
    owner: descriptor.owner,
    valueType: descriptor.definition.type,
  });
}

function descriptorForLegacySnapshot(
  field: ShareFieldDefinitionSnapshot,
): FrozenQuotationFieldDescriptor {
  const sourceBinding = OUTPUT_SOURCE_BINDINGS[field.fieldId];
  const placement = field.placement;
  if (sourceBinding) {
    if (placement && placement !== sourceBinding.placement)
      throw new Error(`历史输出来源字段位置不匹配：${field.fieldId}`);
    return fieldDescriptor(field, sourceBinding.placement, sourceBinding);
  }
  if (!field.fieldId.startsWith('definition:'))
    throw new Error(`历史快照含未知字段身份：${field.fieldId}`);
  const fieldKey = field.fieldId.slice('definition:'.length);
  const system = createSystemFieldRegistry().find(
    (candidate) => candidate.definition.key === fieldKey,
  );
  if (!system)
    throw new Error(
      `历史快照字段无可证明的稳定 Registry 绑定：${field.fieldId}`,
    );
  const expectedPlacement =
    system.binding.quotePlacement ??
    quotePlacementForOwner(system.owner, fieldKey);
  if (!expectedPlacement || (placement && placement !== expectedPlacement))
    throw new Error(`历史快照字段 owner 或位置无法证明：${field.fieldId}`);
  if (
    system.definition.type !== field.type ||
    system.definition.sourceKind !== field.sourceKind
  )
    throw new Error(`历史快照字段类型或来源无法证明：${field.fieldId}`);
  return fieldDescriptor(field, expectedPlacement, {
    fieldKey,
    owner: system.owner,
    valueType: system.definition.type,
  });
}

function publicItemValue(
  item: PublicShareItemSnapshot,
  descriptor: FrozenQuotationFieldDescriptor,
): unknown {
  const explicit = item.fieldValues?.[descriptor.fieldId];
  if (explicit !== undefined) return explicit;
  switch (descriptor.fieldKey) {
    case 'device_image':
      return item.image;
    case 'device_name':
      return item.name;
    case 'device_sku':
      return item.sku;
    case 'quantity':
      return item.quantity;
    case 'item_discount':
      return item.discount;
    case 'quote_item_device_id':
      return item.deviceId;
    case 'quote_item_source_type':
      return item.sourceType;
    case 'quote_item_source_id':
      return item.sourceId;
    case 'quote_item_source_name':
      return item.sourceName;
    case 'quote_item_remark':
      return item.remark;
    case 'product_final_discount_price':
      try {
        return quoteTotal({ items: [item], overallDiscount: 100 });
      } catch {
        return undefined;
      }
    default:
      return undefined;
  }
}

function itemRow(
  item: PublicShareItemSnapshot,
  fields: readonly FrozenQuotationFieldDescriptor[],
): FrozenQuotationListRow {
  return {
    rowId: item.itemId,
    rowType: 'quote-line',
    values: Object.fromEntries(
      fields.map((descriptor) => [
        descriptor.fieldId,
        cell(publicItemValue(item, descriptor), descriptor.valueType),
      ]),
    ),
  };
}

function listRowsFromProjection(
  projection: QuoteChannelProjection,
  fields: readonly FrozenQuotationFieldDescriptor[],
): FrozenQuotationListRow[] {
  const fieldsByRowType = {
    'quote-line': fields.filter((field) => field.placement === 'quote-line'),
    'package-summary': fields.filter(
      (field) => field.placement === 'package-summary',
    ),
  } as const;
  return projection.rows.flatMap((row) => {
    if (row.rowType !== 'quote-line' && row.rowType !== 'package-summary')
      return [];
    const rowFields = fieldsByRowType[row.rowType];
    if (rowFields.length === 0) return [];
    assertNonEmptyString(row.scopeId, '冻结报价清单行缺少稳定行身份');
    return [
      {
        rowId: row.scopeId,
        rowType: row.rowType,
        values: Object.fromEntries(
          rowFields.map((descriptor) => {
            if (!(descriptor.fieldId in row.values))
              throw new Error(
                `冻结报价清单行缺少字段值：${descriptor.fieldId}`,
              );
            return [
              descriptor.fieldId,
              cell(row.values[descriptor.fieldId], descriptor.valueType),
            ];
          }),
        ),
      },
    ];
  });
}

function headerProjectionFromChannel(
  projection: QuoteChannelProjection,
  fields: readonly FrozenQuotationFieldDescriptor[],
): FrozenQuotationHeaderProjection {
  if (
    !projection.customerSnapshot ||
    projection.validUntilSnapshot === undefined ||
    projection.overallDiscountSnapshot === undefined
  )
    throw new Error('公开链接缺少冻结抬头事实');
  const headerRow = projection.rows.find(
    (row) => row.rowType === 'quote-header',
  );
  const values = Object.fromEntries(
    fields.map((descriptor) => {
      const value = headerRow?.values[descriptor.fieldId];
      if (value === undefined)
        throw new Error(`冻结报价抬头缺少字段值：${descriptor.fieldId}`);
      return [descriptor.fieldId, cell(value, descriptor.valueType)];
    }),
  );
  return {
    kind: 'frozen-quotation-header-v1',
    channelId: 'channel:public-link',
    quoteId: projection.quoteId,
    quoteVersion: projection.quoteVersion,
    fields: structuredClone([...fields]),
    values,
    customerSnapshot: structuredClone(projection.customerSnapshot),
    validUntilSnapshot: projection.validUntilSnapshot,
    overallDiscountSnapshot: projection.overallDiscountSnapshot,
    totalSnapshot: quoteTotal({
      items: projection.publicItems,
      overallDiscount: projection.overallDiscountSnapshot,
    }),
  };
}

/**
 * Publication-time builder.  Registry is consulted only here to prove the
 * field binding; values and labels come from the exact channel projection.
 */
export function freezePublicQuotationProjections(
  state: CpqState,
  projection: QuoteChannelProjection,
  combinationSnapshot: ShareFieldCombinationSnapshot,
): FrozenProjectionBundle {
  if (projection.channelId !== 'channel:public-link')
    throw new Error('冻结报价清单只允许客户公开链接通道');
  const snapshots = fieldSnapshotById(combinationSnapshot);
  const columns = new Map(
    projection.columns.map((column) => [column.fieldId, column]),
  );
  if (
    projection.columns.some(
      (column) =>
        !combinationSnapshot.fieldIds.includes(column.fieldId) ||
        !projection.fieldIds.includes(column.fieldId),
    )
  )
    throw new Error('公开通道列未绑定冻结字段组合');
  const selected = selectedFieldIds(combinationSnapshot);
  const neededFieldIds = new Set([
    ...selected,
    ...projection.columns
      .filter((column) => column.placement === 'quote-header')
      .map((column) => column.fieldId),
  ]);
  const descriptors = new Map(
    projection.columns
      .filter(({ fieldId }) => neededFieldIds.has(fieldId))
      .map(({ fieldId, placement }) => {
        const snapshot = snapshots.get(fieldId);
        const column = columns.get(fieldId);
        if (!snapshot || !column)
          throw new Error(`公开通道字段缺少冻结定义：${fieldId}`);
        if (
          snapshot.label !== column.label ||
          snapshot.placement !== column.placement
        )
          throw new Error(`公开通道字段定义与列投影不一致：${fieldId}`);
        if (!placement) throw new Error(`公开通道字段缺少稳定位置：${fieldId}`);
        return [
          fieldId,
          descriptorForCurrentProjection(state, snapshot, placement),
        ];
      }),
  );
  const listFieldIds = selected.filter((fieldId) => {
    const descriptor = descriptors.get(fieldId);
    if (!descriptor) throw new Error(`选择字段未进入公开通道：${fieldId}`);
    return isDescriptorPlacementForList(descriptor.placement);
  });
  if (listFieldIds.length === 0)
    throw new Error('客户字段组合未选择报价清单字段');
  const listFields = listFieldIds.map((fieldId) => {
    const descriptor = descriptors.get(fieldId);
    if (!descriptor) throw new Error(`冻结清单字段丢失：${fieldId}`);
    return descriptor;
  });
  const headerFields = projection.columns.flatMap(({ fieldId }) => {
    const descriptor = descriptors.get(fieldId);
    return descriptor?.placement === 'quote-header' ? [descriptor] : [];
  });
  const quotationListProjection: FrozenQuotationListProjection = {
    kind: 'frozen-quotation-list-v1',
    channelId: 'channel:public-link',
    quoteId: projection.quoteId,
    quoteVersion: projection.quoteVersion,
    combinationSnapshot: structuredClone(combinationSnapshot),
    fieldIds: [...listFieldIds],
    fields: structuredClone(listFields),
    rows: listRowsFromProjection(projection, listFields),
  };
  const headerProjection = headerProjectionFromChannel(
    projection,
    headerFields,
  );
  assertFrozenQuotationProjectionPair(
    quotationListProjection,
    headerProjection,
  );
  return { quotationListProjection, headerProjection };
}

function legacyHeaderValue(
  descriptor: FrozenQuotationFieldDescriptor,
  headerRows: readonly QuotePlacementValueSnapshot[],
  customerSnapshot: FrozenCustomerSnapshot,
  validUntilSnapshot: string,
  overallDiscountSnapshot: number,
  businessModeLabelSnapshot?: string,
): unknown {
  const explicit = headerRows.find((row) => row.placement === 'quote-header')
    ?.values[descriptor.fieldId];
  if (explicit !== undefined) return explicit;
  switch (descriptor.fieldKey) {
    case 'customer_name':
      return customerSnapshot.name;
    case 'customer_contact':
      return customerSnapshot.contact;
    case 'customer_phone':
      return customerSnapshot.phone;
    case 'valid_until':
      return validUntilSnapshot;
    case 'overall_discount':
      return overallDiscountSnapshot;
    case 'business_mode':
      return businessModeLabelSnapshot;
    default:
      return undefined;
  }
}

/**
 * Compatibility migration from an older publication's own frozen facts.
 * It intentionally never accepts a current Quote or catalog row as a source
 * of values.  Historical custom identities without a stored binding are
 * rejected rather than guessed.
 */
export function migrateFrozenQuotationProjectionsFromFacts(input: {
  quoteId: string;
  quoteVersion: number;
  combinationSnapshot: ShareFieldCombinationSnapshot;
  itemsSnapshot: PublicShareItemSnapshot[];
  placementRowsSnapshot?: QuotePlacementValueSnapshot[];
  customerSnapshot: FrozenCustomerSnapshot;
  validUntilSnapshot: string;
  overallDiscountSnapshot: number;
  businessModeLabelSnapshot?: string;
}): FrozenProjectionBundle {
  assertNonEmptyString(input.quoteId, '历史发布缺少报价身份');
  if (!Number.isInteger(input.quoteVersion) || input.quoteVersion < 1)
    throw new Error('历史发布缺少报价版本');
  const snapshots = fieldSnapshotById(input.combinationSnapshot);
  const descriptors = new Map(
    input.combinationSnapshot.fieldIds.map((fieldId) => {
      const snapshot = snapshots.get(fieldId);
      if (!snapshot) throw new Error(`历史字段定义缺失：${fieldId}`);
      return [fieldId, descriptorForLegacySnapshot(snapshot)];
    }),
  );
  const selected = selectedFieldIds(input.combinationSnapshot);
  const listFieldIds = selected.filter((fieldId) => {
    const descriptor = descriptors.get(fieldId);
    if (!descriptor) throw new Error(`历史选择字段缺少稳定绑定：${fieldId}`);
    return isDescriptorPlacementForList(descriptor.placement);
  });
  if (listFieldIds.length === 0)
    throw new Error('历史客户字段组合缺少报价清单字段');
  const listFields = listFieldIds.map((fieldId) => {
    const descriptor = descriptors.get(fieldId);
    if (!descriptor) throw new Error(`历史清单字段缺少稳定绑定：${fieldId}`);
    return descriptor;
  });
  if (
    !input.itemsSnapshot.every(
      (item) => typeof item.itemId === 'string' && item.itemId.trim(),
    )
  )
    throw new Error('历史发布报价行身份无效');
  const packageFields = listFields.filter(
    (field) => field.placement === 'package-summary',
  );
  const packageRows = (input.placementRowsSnapshot ?? [])
    .filter((row) => row.placement === 'package-summary')
    .map((row) => {
      assertNonEmptyString(row.scopeId, '历史报价汇总行缺少稳定身份');
      return {
        rowId: row.scopeId,
        rowType: 'package-summary' as const,
        values: Object.fromEntries(
          packageFields.map((descriptor) => [
            descriptor.fieldId,
            cell(row.values[descriptor.fieldId], descriptor.valueType),
          ]),
        ),
      };
    });
  const quotationListProjection: FrozenQuotationListProjection = {
    kind: 'frozen-quotation-list-v1',
    channelId: 'channel:public-link',
    quoteId: input.quoteId,
    quoteVersion: input.quoteVersion,
    combinationSnapshot: structuredClone(input.combinationSnapshot),
    fieldIds: [...listFieldIds],
    fields: structuredClone(listFields),
    rows: [
      ...input.itemsSnapshot.map((item) =>
        itemRow(
          item,
          listFields.filter((field) => field.placement === 'quote-line'),
        ),
      ),
      ...packageRows,
    ],
  };
  const headerFields = input.combinationSnapshot.fieldIds.flatMap((fieldId) => {
    const descriptor = descriptors.get(fieldId);
    return descriptor?.placement === 'quote-header' ? [descriptor] : [];
  });
  const headerProjection: FrozenQuotationHeaderProjection = {
    kind: 'frozen-quotation-header-v1',
    channelId: 'channel:public-link',
    quoteId: input.quoteId,
    quoteVersion: input.quoteVersion,
    fields: structuredClone(headerFields),
    values: Object.fromEntries(
      headerFields.map((descriptor) => [
        descriptor.fieldId,
        cell(
          legacyHeaderValue(
            descriptor,
            input.placementRowsSnapshot ?? [],
            input.customerSnapshot,
            input.validUntilSnapshot,
            input.overallDiscountSnapshot,
            input.businessModeLabelSnapshot,
          ),
          descriptor.valueType,
        ),
      ]),
    ),
    customerSnapshot: structuredClone(input.customerSnapshot),
    validUntilSnapshot: input.validUntilSnapshot,
    overallDiscountSnapshot: input.overallDiscountSnapshot,
    totalSnapshot: quoteTotal({
      items: input.itemsSnapshot,
      overallDiscount: input.overallDiscountSnapshot,
    }),
  };
  assertFrozenQuotationProjectionPair(
    quotationListProjection,
    headerProjection,
  );
  return { quotationListProjection, headerProjection };
}

/** Reprojects only editable customer-side item facts through the frozen schema. */
export function quotationListProjectionForPublicItems(
  projection: FrozenQuotationListProjection,
  items: readonly PublicShareItemSnapshot[],
): FrozenQuotationListProjection {
  const issue = frozenQuotationListProjectionIssue(projection);
  if (issue) throw new Error(issue);
  const lineFields = projection.fields.filter(
    (field) => field.placement === 'quote-line',
  );
  return {
    ...structuredClone(projection),
    rows: [
      ...items.map((item) => itemRow(item, lineFields)),
      ...projection.rows.filter((row) => row.rowType === 'package-summary'),
    ],
  };
}

/** The SharePage delegates editable quantity mapping to this frozen schema. */
export function updatePublicShareItemsFromFrozenCell(
  projection: FrozenQuotationListProjection,
  items: readonly PublicShareItemSnapshot[],
  input: { rowId: string; fieldId: string; value: number },
): PublicShareItemSnapshot[] {
  const issue = frozenQuotationListProjectionIssue(projection);
  if (issue) throw new Error(issue);
  const field = projection.fields.find(
    (candidate) => candidate.fieldId === input.fieldId,
  );
  if (
    field?.customerEdit !== 'quantity' ||
    field?.valueType !== 'number' ||
    !Number.isInteger(input.value) ||
    input.value < 1
  )
    throw new Error('客户不能修改该冻结报价字段');
  let found = false;
  const next = items.map((item) => {
    if (item.itemId !== input.rowId) return item;
    found = true;
    return { ...item, quantity: input.value };
  });
  if (!found) throw new Error('客户修改目标不属于冻结报价清单');
  return next;
}

function descriptorIssue(
  descriptor: unknown,
  combination: ShareFieldCombinationSnapshot,
  expectedPlacement?: QuoteFieldPlacement,
): string | undefined {
  if (!descriptor || typeof descriptor !== 'object')
    return '冻结报价字段描述无效';
  const value = descriptor as Partial<FrozenQuotationFieldDescriptor>;
  if (
    typeof value.fieldId !== 'string' ||
    !value.fieldId.trim() ||
    typeof value.fieldKey !== 'string' ||
    !value.fieldKey.trim() ||
    !FIELD_OWNERS.includes(value.owner as CustomFieldOwner) ||
    !QUOTE_FIELD_TYPES.includes(value.valueType as QuoteFieldType) ||
    typeof value.label !== 'string' ||
    !value.label.trim() ||
    !['normal', 'long'].includes(value.width ?? '') ||
    !QUOTE_FIELD_PLACEMENTS.includes(value.placement as QuoteFieldPlacement) ||
    !['system', 'manual'].includes(value.sourceKind ?? '') ||
    !['image', 'value'].includes(value.presentation ?? '') ||
    !['read-only', 'quantity'].includes(value.customerEdit ?? '') ||
    (expectedPlacement !== undefined && value.placement !== expectedPlacement)
  )
    return '冻结报价字段描述形态无效';
  const snapshot = combination.fields.find(
    (field) => field.fieldId === value.fieldId,
  );
  if (
    !snapshot ||
    snapshot.label !== value.label ||
    snapshot.type !== value.valueType ||
    snapshot.width !== value.width ||
    snapshot.sourceKind !== value.sourceKind ||
    (snapshot.placement !== undefined && snapshot.placement !== value.placement)
  )
    return `冻结报价字段定义不匹配：${value.fieldId}`;
  const source = OUTPUT_SOURCE_BINDINGS[value.fieldId];
  if (source) {
    if (
      source.fieldKey !== value.fieldKey ||
      source.owner !== value.owner ||
      source.valueType !== value.valueType ||
      source.placement !== value.placement
    )
      return `冻结输出来源绑定不匹配：${value.fieldId}`;
  } else if (
    !value.fieldId.startsWith('definition:') ||
    value.fieldId.slice('definition:'.length) !== value.fieldKey
  ) {
    return `冻结报价字段身份未知：${value.fieldId}`;
  } else {
    const system = createSystemFieldRegistry().find(
      (candidate) => candidate.definition.key === value.fieldKey,
    );
    if (system) {
      const placement =
        system.binding.quotePlacement ??
        quotePlacementForOwner(system.owner, value.fieldKey);
      const acceptsLegacyPackageOwner =
        value.fieldKey === 'package_discount_quote_tax13' &&
        value.owner === 'package' &&
        value.placement === 'package-summary' &&
        system.owner === 'quote-line';
      if (
        (value.owner !== system.owner && !acceptsLegacyPackageOwner) ||
        value.valueType !== system.definition.type ||
        value.sourceKind !== system.definition.sourceKind ||
        (value.placement !== placement && !acceptsLegacyPackageOwner)
      )
        return `冻结系统字段 Registry 绑定不匹配：${value.fieldId}`;
    } else if (
      !value.fieldKey.startsWith('field.custom.') ||
      value.sourceKind !== 'manual'
    ) {
      return `冻结报价字段缺少可证明的 Registry 身份：${value.fieldId}`;
    }
  }
  if (
    value.presentation === 'image' &&
    !(value.fieldKey === 'device_image' && value.valueType === 'text')
  )
    return `冻结图片字段绑定无效：${value.fieldId}`;
  if (
    value.customerEdit === 'quantity' &&
    !(value.fieldKey === 'quantity' && value.valueType === 'number')
  )
    return `冻结客户可编辑字段绑定无效：${value.fieldId}`;
  return undefined;
}

function rowIssue(
  row: unknown,
  fields: readonly FrozenQuotationFieldDescriptor[],
): string | undefined {
  if (!row || typeof row !== 'object') return '冻结报价行无效';
  const value = row as Partial<FrozenQuotationListRow>;
  if (
    typeof value.rowId !== 'string' ||
    !value.rowId.trim() ||
    (value.rowType !== 'quote-line' && value.rowType !== 'package-summary') ||
    !value.values ||
    typeof value.values !== 'object'
  )
    return '冻结报价行形态无效';
  const expected = fields
    .filter((field) => field.placement === value.rowType)
    .map((field) => field.fieldId);
  const actual = Object.keys(value.values);
  if (!sameStringOrder(actual, expected)) return '冻结报价行字段顺序或范围无效';
  for (const field of fields.filter(
    (item) => item.placement === value.rowType,
  )) {
    const fieldValue = value.values[field.fieldId];
    if (
      !fieldValue ||
      typeof fieldValue !== 'object' ||
      fieldValue.valueType !== field.valueType ||
      !isQuotedValue(fieldValue.value) ||
      !isValueForType(fieldValue.value, field.valueType)
    )
      return `冻结报价行字段值类型无效：${field.fieldId}`;
  }
  return undefined;
}

export function frozenQuotationListProjectionIssue(
  projection: unknown,
): string | undefined {
  if (!projection || typeof projection !== 'object')
    return '冻结报价清单不存在';
  const value = projection as Partial<FrozenQuotationListProjection>;
  if (
    value.kind !== 'frozen-quotation-list-v1' ||
    value.channelId !== 'channel:public-link' ||
    typeof value.quoteId !== 'string' ||
    !value.quoteId.trim() ||
    !Number.isInteger(value.quoteVersion) ||
    Number(value.quoteVersion) < 1 ||
    !value.combinationSnapshot ||
    !Array.isArray(value.fieldIds) ||
    !Array.isArray(value.fields) ||
    !Array.isArray(value.rows)
  )
    return '冻结报价清单形态无效';
  try {
    const snapshotFields = fieldSnapshotById(value.combinationSnapshot);
    const selected = selectedFieldIds(value.combinationSnapshot);
    const expectedIds = selected.filter((fieldId) => {
      const field = snapshotFields.get(fieldId);
      return field?.placement !== 'quote-header';
    });
    if (
      !sameStringOrder(value.fieldIds, expectedIds) ||
      value.fields.length !== value.fieldIds.length ||
      !sameStringOrder(
        value.fields.map((field) => field.fieldId),
        value.fieldIds,
      )
    )
      return '冻结报价清单字段范围或顺序无效';
    for (const descriptor of value.fields) {
      const issue = descriptorIssue(descriptor, value.combinationSnapshot);
      if (issue) return issue;
      if (!isDescriptorPlacementForList(descriptor.placement))
        return '冻结报价清单混入非清单字段';
    }
    if (new Set(value.rows.map((row) => row.rowId)).size !== value.rows.length)
      return '冻结报价清单行身份重复';
    for (const row of value.rows) {
      const issue = rowIssue(row, value.fields);
      if (issue) return issue;
    }
    return undefined;
  } catch (error) {
    return error instanceof Error ? error.message : '冻结报价清单校验失败';
  }
}

export function frozenQuotationHeaderProjectionIssue(
  projection: unknown,
  combination: ShareFieldCombinationSnapshot,
): string | undefined {
  if (!projection || typeof projection !== 'object')
    return '冻结报价抬头不存在';
  const value = projection as Partial<FrozenQuotationHeaderProjection>;
  if (
    value.kind !== 'frozen-quotation-header-v1' ||
    value.channelId !== 'channel:public-link' ||
    typeof value.quoteId !== 'string' ||
    !value.quoteId.trim() ||
    !Number.isInteger(value.quoteVersion) ||
    Number(value.quoteVersion) < 1 ||
    !Array.isArray(value.fields) ||
    !value.values ||
    typeof value.values !== 'object' ||
    !value.customerSnapshot ||
    typeof value.customerSnapshot.customerId !== 'string' ||
    !value.customerSnapshot.customerId.trim() ||
    typeof value.customerSnapshot.customerNo !== 'string' ||
    !value.customerSnapshot.customerNo.trim() ||
    typeof value.customerSnapshot.name !== 'string' ||
    !value.customerSnapshot.name.trim() ||
    typeof value.customerSnapshot.contact !== 'string' ||
    typeof value.customerSnapshot.phone !== 'string' ||
    typeof value.validUntilSnapshot !== 'string' ||
    !Number.isFinite(value.overallDiscountSnapshot) ||
    !Number.isFinite(value.totalSnapshot)
  )
    return '冻结报价抬头形态无效';
  const expectedIds = value.fields.map((field) => field.fieldId);
  if (
    new Set(expectedIds).size !== expectedIds.length ||
    !sameStringOrder(Object.keys(value.values), expectedIds) ||
    expectedIds.some((fieldId) => !combination.fieldIds.includes(fieldId)) ||
    !sameStringOrder(
      expectedIds,
      combination.fieldIds.filter((fieldId) => expectedIds.includes(fieldId)),
    )
  )
    return '冻结报价抬头字段范围或顺序无效';
  for (const descriptor of value.fields) {
    const issue = descriptorIssue(descriptor, combination, 'quote-header');
    if (issue) return issue;
    const fieldValue = value.values[descriptor.fieldId];
    if (
      !fieldValue ||
      fieldValue.valueType !== descriptor.valueType ||
      !isQuotedValue(fieldValue.value) ||
      !isValueForType(fieldValue.value, descriptor.valueType)
    )
      return `冻结报价抬头字段值类型无效：${descriptor.fieldId}`;
  }
  return undefined;
}

export function assertFrozenQuotationProjectionPair(
  list: FrozenQuotationListProjection,
  header: FrozenQuotationHeaderProjection,
): void {
  const listIssue = frozenQuotationListProjectionIssue(list);
  if (listIssue) throw new Error(listIssue);
  const headerIssue = frozenQuotationHeaderProjectionIssue(
    header,
    list.combinationSnapshot,
  );
  if (headerIssue) throw new Error(headerIssue);
  if (
    list.quoteId !== header.quoteId ||
    list.quoteVersion !== header.quoteVersion ||
    list.channelId !== header.channelId
  )
    throw new Error('冻结报价清单与抬头跨版本或通道错绑');
}
